import { gql } from "@urql/core"
import React, { useState } from "react"
import { useMutation } from "urql"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Button } from "../../../components/shared/Buttons"
import { ButtonLink } from "../../../components/shared/Links"
import { EditableValue } from "../../../components/shared/Inputs"
import { Banner } from "../../../components/shared/Layout"
import { AnimatedModal } from "../../../components/shared/Modal"
import Typography from "../../../components/shared/Typography"
import { ToastProvider, useToast } from "../../../contexts/ToastContext"
import GraphQLProvider from "../../../providers/GraphQLProvider"

const DISCONNECT_SQUARE_MUTATION = gql`
  mutation DisconnectSquareAccount {
    disconnectSquareAccount {
      result
      errors
    }
  }
`

const SquareSettings = ({ squareInfo }) => {
  const { showToast } = useToast()
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  const [, disconnectSquare] = useMutation(DISCONNECT_SQUARE_MUTATION)

  if (squareInfo.message) {
    showToast(squareInfo.message)
  }
  if (squareInfo.error) {
    showToast({
      content: squareInfo.error,
      type: "error",
      duration: 5000
    })
  }

  const handleDisconnect = async () => {
    setDisconnecting(true)
    try {
      const res = await disconnectSquare()
      setShowDisconnectModal(false)

      if (res.data?.disconnectSquareAccount?.result === "success") {
        showToast("Square account disconnected successfully", "success")
        window.location.href = "/portal/settings/banking"
      } else {
        showToast(res.data?.disconnectSquareAccount?.errors?.[0] || "Failed to disconnect Square", "error")
      }
    } finally {
      setDisconnecting(false)
    }
  }

  const connected = squareInfo.squareConnected
  const needsReconnect = squareInfo.erroredAt

  return (
    <div>
      <Breadcrumbs
        links={[
          { href: "/portal/settings", text: "Settings" },
          { href: "/portal/settings/banking", text: "Payments" }
        ]}
        activePage="Square"
      />
      <div className="mb-9 flex flex-col sm:mb-4">
        <Typography as="h1" variant="h3">
          Square
        </Typography>
      </div>
      <div className="w-full max-w-[750px] rounded-xl border border-gray-light p-8 sm:w-full">
        {!connected && !needsReconnect && (
          <>
            <Typography variant="body" className="mb-4 text-gray-dark">
              Connect Square so client payments are deposited to your Square account.
            </Typography>
            <ButtonLink href="/portal/settings/banking/square/authorize" className="w-fit">
              Connect Square
            </ButtonLink>
          </>
        )}

        {connected && (
          <>
            {needsReconnect && (
              <Banner type="error" className="mb-6">
                Heal.me no longer has access to your Square account. Reconnect to continue using Square, or disconnect
                to stop offering Square checkout.
              </Banner>
            )}
            {squareInfo.disconnectBlockers?.length > 0 && (
              <Banner type="warning" className="mb-6">
                <ul className="list-disc space-y-2 pl-5">
                  {squareInfo.disconnectBlockers.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </Banner>
            )}
            <Typography variant="h5" className="mb-4">
              {needsReconnect ? "Square connection issue" : "Connected to Square"}
            </Typography>
            <div className="mb-6 space-y-3">
              <EditableValue
                value="Client payments will be made to this account."
                editable={false}
                name={squareInfo.displayName || "Square account"}
              />
              {squareInfo.merchantId && (
                <Typography variant="small" className="text-gray-dark">
                  Merchant ID: {squareInfo.merchantId}
                </Typography>
              )}
              {squareInfo.locationId && (
                <Typography variant="small" className="text-gray-dark">
                  Location ID: {squareInfo.locationId}
                </Typography>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <ButtonLink
                href="/portal/settings/banking/square/authorize"
                variant={needsReconnect ? "primary" : "secondary"}
                className="w-fit">
                {needsReconnect ? "Reconnect Square" : "Reconnect"}
              </ButtonLink>
              <Button
                type="secondary"
                className="w-fit"
                disabled={(squareInfo.disconnectBlockers?.length ?? 0) > 0 || disconnecting}
                onClick={() => setShowDisconnectModal(true)}>
                Disconnect Square
              </Button>
            </div>
          </>
        )}
      </div>

      <AnimatedModal
        visible={showDisconnectModal}
        hideModal={() => setShowDisconnectModal(false)}
        header="Disconnect Square"
        showFooter={true}
        actionButtonCopy="Yes, disconnect"
        actionButtonType="destructive"
        onSave={handleDisconnect}>
        <Typography variant="body">
          Clients will no longer be able to pay with Square through Heal.me, and saved Square cards will stop working.
          You can reconnect Square later from Payments settings.
        </Typography>
      </AnimatedModal>
    </div>
  )
}

export default function SquarePaymentsSettings(props) {
  return (
    <GraphQLProvider>
      <ToastProvider>
        <SquareSettings {...props} />
      </ToastProvider>
    </GraphQLProvider>
  )
}
